import type { Account, PaymentMethod, Vendor } from "@/api/types";

/**
 * The last reference data the API successfully returned, kept so the quick-entry
 * screen still renders after a reload with no network.
 *
 * WHY THIS EXISTS — measured, not theoretical. On 2026-09-06 the deployed booth
 * bundle was driven through the exact scripted beat in
 * `docs/demo/tradeshow-2026-09.md` ("Turn on airplane mode. Create another entry —
 * it still works"), with one addition: a RELOAD while offline. The result was a
 * dead end —
 *
 *     "Can't reach the demo API right now. / Failed to fetch / Try again"
 *
 * one button, zero inputs, where the online screen has thirty-three controls. The
 * outbox (of-002) that makes the airplane-mode beat work is unreachable from there,
 * because you cannot compose an entry without the pickers.
 *
 * The gap is narrow and entirely on our side: `useReferenceData` fetched vendors,
 * categories, payment methods and the entity fresh on every mount and had nowhere
 * to fall back to. The service worker precaches the SHELL — so React boots — but
 * the shell with no data is an error screen.
 *
 * WHY THE EXISTING OFFLINE CHECK DID NOT CATCH IT. `deploy/verify-offline-shell.sh`
 * asserts the offline reload renders something with a root element and the word
 * "EasyFarm" in it. The dead-end error screen satisfies both, and the title is
 * "EasyFarm" regardless, so the check reported
 * *"the deployed bundle serves its own shell offline"* — literally true, and
 * useless. `rules/core.md` §5: ask what would make a signal go red.
 *
 * WHY localStorage RATHER THAN `offline/persistentQueue.ts`. That module is a
 * QUEUE — `add`/`remove`/`all`, keyed by `clientId`, ordered by `queuedAt` — built
 * for the outbox, the scan queue and the mapping queue. This is a single snapshot
 * that is overwritten wholesale, and it must be readable SYNCHRONOUSLY: IndexedDB
 * is async, so the first paint would still be the error screen and the fix would
 * flash the very failure it exists to remove. Forcing a queue into this shape would
 * be the parallel-implementation smell in reverse — reusing the name of a thing
 * rather than the thing.
 */

const VERSION = 1;
const KEY_PREFIX = `easyfarm.reference.v${VERSION}.`;

export interface ReferenceSnapshot {
  vendors: Vendor[];
  accounts: Account[];
  paymentMethods: PaymentMethod[];
  entityId: string | null;
  /** Epoch ms. Not used for expiry — see `readReferenceSnapshot`. */
  savedAt: number;
}

/**
 * Keyed by tenant so the Booth Demo and Live Wall bundles can never read each
 * other's pick lists. In the current deployment they are separate origins and so
 * separate storage anyway; that is a property of today's port layout, not a
 * guarantee, and a wrong vendor list on the wall would be invisible until someone
 * read it closely.
 */
function keyFor(tenant: string): string {
  return KEY_PREFIX + tenant;
}

/** Every accessor is guarded: Safari private browsing THROWS on access, and a
 *  storage failure must degrade to "no cache", never take the screen down. */
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function writeReferenceSnapshot(
  tenant: string,
  snapshot: Omit<ReferenceSnapshot, "savedAt">,
): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(keyFor(tenant), JSON.stringify({ ...snapshot, savedAt: Date.now() }));
  } catch {
    // Quota, private mode, or a serialization failure. The app is fully functional
    // right now — it just will not survive an offline reload. Not worth a visible
    // error at a booth.
  }
}

/**
 * NO EXPIRY, deliberately.
 *
 * The obvious instinct is to drop a snapshot older than some window. Vendors,
 * categories and payment methods are seeded once per booth and do not change
 * during a show — `useReferenceData`'s own remark says exactly that, which is why
 * it does not poll. A stale snapshot is therefore the SAME data, and the failure
 * mode of expiring one is the failure mode this module exists to remove: the
 * farmer in the field with no signal gets the dead end again, now on a timer
 * nobody can see. If the lists ever do change mid-session the online path
 * overwrites this within a second of the next successful load.
 *
 * Returns null on anything unexpected rather than throwing — a corrupted entry
 * must not be worse than an absent one.
 */
export function readReferenceSnapshot(tenant: string): ReferenceSnapshot | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(keyFor(tenant));
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<ReferenceSnapshot>;
    // Shape check, not a type assertion. This value survives deploys, so a bundle
    // built today may read what a bundle from a month ago wrote.
    if (!Array.isArray(parsed.vendors) || !Array.isArray(parsed.accounts)) return null;
    // A snapshot with no vendors or no categories cannot complete an entry, so it
    // is worse than useless: it would replace an honest "can't reach the API" with
    // a picker screen that silently cannot submit.
    if (parsed.vendors.length === 0 || parsed.accounts.length === 0) return null;
    return {
      vendors: parsed.vendors,
      accounts: parsed.accounts,
      paymentMethods: Array.isArray(parsed.paymentMethods) ? parsed.paymentMethods : [],
      entityId: typeof parsed.entityId === "string" ? parsed.entityId : null,
      savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : 0,
    };
  } catch {
    return null;
  }
}

/** Used by the booth reset so a reset tenant cannot serve pick lists from before it. */
export function clearReferenceSnapshot(tenant: string): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(keyFor(tenant));
  } catch {
    /* nothing to do — see writeReferenceSnapshot */
  }
}
